import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { listPendingActivations, decideActivation } from "@/lib/moderation.functions";

export const Route = createFileRoute("/_authenticated/admin/moderation")({
  head: () => ({
    meta: [{ title: "Admin · Moderation Queue" }, { name: "robots", content: "noindex" }],
  }),
  component: ModerationQueue,
});

type Row = Awaited<ReturnType<typeof listPendingActivations>>[number];

function ModerationQueue() {
  const list = useServerFn(listPendingActivations);
  const decide = useServerFn(decideActivation);
  const [rows, setRows] = useState<Row[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});

  const refresh = useCallback(async () => {
    try {
      const r = await list();
      setRows(r as Row[]);
    } catch (e) {
      toast.error((e as Error).message);
    }
  }, [list]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function act(row: Row, decision: "approve" | "reject") {
    setBusyId(row.id);
    try {
      await decide({
        data: { id: row.id, decision, notes: notes[row.id]?.trim() || undefined },
      });
      toast.success(decision === "approve" ? "Approved & scheduled" : "Rejected");
      setRows((prev) => prev.filter((r) => r.id !== row.id));
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section className="mx-auto max-w-5xl px-4 py-10">
      <header className="space-y-2">
        <p className="text-xs uppercase tracking-[0.3em] text-gold">Content Engine</p>
        <h1 className="font-serif text-3xl text-gold-luminous sm:text-4xl">Moderation Queue</h1>
        <p className="text-sm text-muted-foreground">
          Every AI-drafted activation waits here for review. Approving schedules it for its
          publish slot; rejecting sends it back to draft.
        </p>
      </header>

      {rows.length === 0 ? (
        <p className="mt-10 text-sm text-foreground/60">Nothing pending. The current is clear.</p>
      ) : (
        <ul className="mt-8 space-y-6">
          {rows.map((r) => (
            <li key={r.id} className="rounded-2xl border hairline bg-obsidian/40 p-5">
              <div className="flex flex-wrap items-baseline justify-between gap-3">
                <div>
                  <h2 className="font-serif text-xl text-ivory">{r.title}</h2>
                  <p className="text-xs uppercase tracking-widest text-foreground/50">
                    {r.category} · {r.slot} ·{" "}
                    {r.publish_at ? new Date(r.publish_at).toLocaleString() : "unscheduled"}
                    {r.drafted_by_model ? ` · ${r.drafted_by_model}` : ""}
                  </p>
                </div>
                <span className="rounded-full border border-amber-400/40 px-3 py-1 text-[10px] uppercase tracking-widest text-amber-300">
                  Pending
                </span>
              </div>

              {r.subtitle && <p className="mt-2 text-sm text-foreground/80">{r.subtitle}</p>}

              <div className="mt-4 grid gap-3 text-sm text-foreground/85 sm:grid-cols-2">
                <Field label="Intention">{r.intention}</Field>
                <Field label="Phrase">{r.phrase}</Field>
                <Field label="Instruction">{r.instruction}</Field>
                <Field label="Meditation">{r.meditation}</Field>
                <Field label="Transmission">{r.transmission}</Field>
                <Field label="Reflection">{r.reflection_question}</Field>
              </div>

              <textarea
                value={notes[r.id] ?? ""}
                onChange={(e) => setNotes((n) => ({ ...n, [r.id]: e.target.value }))}
                placeholder="Notes for the audit trail (optional)"
                className="mt-4 min-h-[60px] w-full rounded-md border hairline bg-obsidian p-3 text-sm"
              />

              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  onClick={() => act(r, "approve")}
                  disabled={busyId === r.id}
                  className="min-h-[44px]"
                >
                  {busyId === r.id ? "Working…" : "Approve & schedule"}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => act(r, "reject")}
                  disabled={busyId === r.id}
                  className="min-h-[44px]"
                >
                  Reject
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  if (!children) return null;
  return (
    <div>
      <p className="text-[10px] uppercase tracking-widest text-foreground/50">{label}</p>
      <p className="mt-1 whitespace-pre-wrap">{children}</p>
    </div>
  );
}
